import { Link } from "react-router-dom";
import { Sparkles, UserPlus, Camera, MessageCircle, Handshake } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const ICONS = [UserPlus, Camera, MessageCircle, Handshake];

const CONTENT = {
  en: {
    badge: "Simple & free",
    title: "How it works",
    intro: "Buying, adopting or selling on CritterPosts.ca takes just a few easy steps — with tools that keep every connection safe and local.",
    cta_text: "Ready to find or rehome a pet the friendly way?",
    cta_browse: "Browse listings",
    cta_post: "Post a listing",
    steps: [
      { h: "1. Create a free account", p: "Sign up in seconds with your email or Google. You'll need an account to post listings and message other members — browsing is always open to everyone." },
      { h: "2. Post or browse a listing", p: "Sellers add clear photos, a price, location and details — our AI can even help write a great description. Buyers and adopters search by category, province and city, or explore the map to find animals and services nearby." },
      { h: "3. Connect safely", p: "Reach out through secure in-site messaging, or by email and phone when a seller chooses to share them. Ask questions, request health records and get to know each other before you meet." },
      { h: "4. Meet and complete responsibly", p: "Always meet in person in a safe, public place, verify the animal and its records, and never send payment before meeting. When everything feels right, welcome your new companion home — then leave a review to help the community." },
    ],
  },
  fr: {
    badge: "Simple et gratuit",
    title: "Comment ça marche",
    intro: "Acheter, adopter ou vendre sur CritterPosts.ca ne prend que quelques étapes faciles — avec des outils qui gardent chaque échange sûr et local.",
    cta_text: "Prêt à trouver ou à replacer un animal de façon conviviale ?",
    cta_browse: "Parcourir les annonces",
    cta_post: "Publier une annonce",
    steps: [
      { h: "1. Créez un compte gratuit", p: "Inscrivez-vous en quelques secondes avec votre courriel ou Google. Un compte est requis pour publier des annonces et écrire aux autres membres — la navigation reste toujours ouverte à tous." },
      { h: "2. Publiez ou parcourez une annonce", p: "Les vendeurs ajoutent des photos claires, un prix, un emplacement et des détails — notre IA peut même aider à rédiger une belle description. Les acheteurs et adoptants cherchent par catégorie, province et ville, ou explorent la carte pour trouver des animaux et services à proximité." },
      { h: "3. Connectez-vous en toute sécurité", p: "Communiquez via la messagerie sécurisée du site, ou par courriel et téléphone lorsque le vendeur choisit de les partager. Posez des questions, demandez les dossiers de santé et apprenez à vous connaître avant de vous rencontrer." },
      { h: "4. Rencontrez et concluez de façon responsable", p: "Rencontrez toujours la personne dans un lieu public sûr, vérifiez l'animal et ses dossiers, et n'envoyez jamais de paiement avant la rencontre. Lorsque tout vous semble bon, accueillez votre nouveau compagnon — puis laissez un avis pour aider la communauté." },
    ],
  },
};

export default function HowItWorks() {
  const { lang } = useLanguage();
  const c = CONTENT[lang] || CONTENT.en;
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid="how-it-works-page">
      <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#F5551E] bg-[#FFF4F0] rounded-full px-3 py-1"><Sparkles size={13} /> {c.badge}</span>
      <h1 className="font-display font-extrabold text-4xl text-[#12234A] mt-4">{c.title}</h1>
      <p className="text-base text-slate-600 mt-5">{c.intro}</p>

      <div className="mt-8 space-y-4">
        {c.steps.map((s, i) => {
          const Icon = ICONS[i];
          return (
            <section key={i} data-testid={`how-step-${i + 1}`} className="flex gap-4 rounded-2xl border border-slate-100 bg-white p-5">
              <span className="shrink-0 h-11 w-11 rounded-xl bg-[#FFF4F0] flex items-center justify-center text-[#F5551E]"><Icon size={22} /></span>
              <div>
                <h2 className="font-display font-bold text-lg text-[#12234A]">{s.h}</h2>
                <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{s.p}</p>
              </div>
            </section>
          );
        })}
      </div>

      <div className="mt-10 rounded-2xl bg-[#12234A] text-white p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <p className="text-sm text-slate-200">{c.cta_text}</p>
        <div className="flex gap-3">
          <Link to="/browse" data-testid="how-browse-cta" className="rounded-xl bg-white/10 hover:bg-white/20 transition-colors px-5 py-2.5 text-sm font-semibold">{c.cta_browse}</Link>
          <Link to="/post" data-testid="how-post-cta" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] transition-colors px-5 py-2.5 text-sm font-semibold">{c.cta_post}</Link>
        </div>
      </div>
    </div>
  );
}
